import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  queryOptions,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { PageTitle } from '@components/PageTitle';
import { Anchor } from '@components/ui/anchor';
import { Button } from '@components/ui/button';
import { Separator } from '@components/ui/separator';
import { queryKeys } from '@constants/queryKeys.constant';
import { serviceFieldTooltips } from '@constants/services.constant';
import { EStaleTime } from '@enums/staleTime.enum';
import { removeUnderscore } from '@formatters/removeUnderscore.formatter';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import { businessLineService } from '@services/businessLines/service';
import {
  createServiceSchema,
  type CreateServicePayload,
} from '@services/services/dtos/create.dto';
import { serviceService } from '@services/services/service';
import type { CreateServiceOutput } from '@services/services/types/create.type';
import type {
  GetAllServiceData,
  GetAllServicesOutput,
} from '@services/services/types/getAll.type';
import { serviceWorkflowService } from '@services/serviceWorkflows/service';

function businessLineQueryOptions(id: string) {
  return queryOptions({
    queryFn: () => businessLineService.getById(id),
    queryKey: queryKeys.businessLines.detail(id),
    staleTime: EStaleTime.OneHour,
  });
}

export const Route = createFileRoute(
  '/_layout/(private)/business-lines/$id/services/create',
)({
  loader: ({ context, params }) => {
    const options = businessLineQueryOptions(params.id);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
  component: CreateService,
});

function CreateService() {
  const { id } = Route.useParams();
  const navigate = Route.useNavigate();
  const { queryClient } = Route.useRouteContext();

  const { data } = useSuspenseQuery(businessLineQueryOptions(id));

  const serviceTypeOptions = (data.businessLine.allowedServiceTypes ?? []).map(
    serviceType => ({
      label: removeUnderscore(serviceType.name, { capitalize: true }),
      value: serviceType.id,
    }),
  );

  const form = useForm<CreateServicePayload>({
    defaultValues: {
      businessLineId: id,
      serviceTypeId: '',
      serviceWorkflowId: '',
      translations: { en: '', fr: '', nl: '' },
    },
    mode: 'onBlur',
    resolver: zodResolver(createServiceSchema),
  });

  const { isPending, mutate: createService } = useMutation<
    CreateServiceOutput,
    unknown,
    CreateServicePayload
  >({
    mutationFn: serviceService.create,
    onError: handleRequestError,
    onSuccess: ({ service }) => {
      queryClient.setQueriesData<GetAllServicesOutput>(
        { queryKey: queryKeys.services.listByBusinessLine(id) },
        old => {
          if (!old) {
            return old;
          }

          if (old.pagination.page !== 1) {
            return { ...old, count: old.count + 1 };
          }

          const newItem: GetAllServiceData = {
            createdAt: service.createdAt,
            displayId: service.displayId,
            id: service.id,
            status: service.status,
            translations: service.translations,
            type: service.type,
            workflow: service.workflow,
          };

          return {
            ...old,
            count: old.count + 1,
            list: [newItem, ...old.list].slice(0, 10),
          };
        },
      );

      navigate({ params: { id }, replace: true, to: '/business-lines/$id' });
      toast.success('Success', {
        description: 'Service created successfully.',
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    createService(values);
  });

  return (
    <Form.Provider {...form}>
      <PageTitle hasBackButton title="Create Service" />

      <form
        className="mx-auto mt-5 space-y-3 p-2 md:w-4/5 2xl:w-3/5"
        onSubmit={handleSubmit}
      >
        <div className="grid gap-2 sm:grid-cols-3">
          <InputControlled
            label="Translation (EN)"
            name="translations.en"
            tooltip={serviceFieldTooltips.translationEn}
          />
          <InputControlled
            label="Translation (FR)"
            name="translations.fr"
            tooltip={serviceFieldTooltips.translationFr}
          />
          <InputControlled
            label="Translation (NL)"
            name="translations.nl"
            tooltip={serviceFieldTooltips.translationNl}
          />
        </div>

        <Separator variant="form" />

        <div className="grid gap-2 sm:grid-cols-2">
          <InputControlled
            label="Service type"
            name="serviceTypeId"
            options={serviceTypeOptions}
            tooltip={serviceFieldTooltips.serviceType}
            variant="select"
          />
          <ServiceWorkflowSelect />
        </div>

        <div className="mt-10 flex justify-center gap-x-2">
          <Anchor
            className="min-w-40 uppercase"
            disabled={isPending}
            params={{
              id,
            }}
            rounded="lg"
            size="lg"
            to="/business-lines/$id"
            variant="destructive"
          >
            Cancel
          </Anchor>
          <Button
            className="min-w-52"
            isLoading={isPending}
            size="lg"
            type="submit"
          >
            Create
          </Button>
        </div>
      </form>
    </Form.Provider>
  );
}

function ServiceWorkflowSelect() {
  const selectedServiceTypeId = useWatch<CreateServicePayload, 'serviceTypeId'>(
    { name: 'serviceTypeId' },
  );
  const { resetField } = useFormContext<CreateServicePayload>();

  useEffect(() => {
    resetField('serviceWorkflowId');
  }, [selectedServiceTypeId, resetField]);

  const {
    data: serviceWorkflowOptions,
    isFetching: isServiceWorkflowsFetching,
  } = useQuery({
    enabled: !!selectedServiceTypeId,
    queryFn: () =>
      serviceWorkflowService.getAllToSelect({
        serviceTypeId: selectedServiceTypeId,
      }),
    queryKey: queryKeys.serviceWorkflows.select(selectedServiceTypeId),
    staleTime: EStaleTime.OneDay,
  });

  return (
    <InputControlled
      disabled={!selectedServiceTypeId || isServiceWorkflowsFetching}
      label="Workflow"
      name="serviceWorkflowId"
      options={serviceWorkflowOptions?.list ?? []}
      tooltip={serviceFieldTooltips.workflow}
      variant="select"
    />
  );
}
